import React, {Component, Fragment, ReactElement} from "react";
import {DefaultButton, Dropdown, IDropdownOption, TextField} from "office-ui-fabric-react";
import ApiService from "../ApiService";
import DispatchProps from "../../utils/DispatchProps";
import {DurableFunctionAppName, DurableFunctionInstance} from "../../models/DurableFunctionInstance";
import MailroomDurableFunctionInstanceList from "../mailroom/MailroomDurableFunctionInstanceList";
import {TableColumn} from "../Table";
import TableCell from "../TableCell";
import * as H from "history";
import {parseQueryString} from "../../utils/Utils";
import {debounce} from "throttle-debounce";
import StartOrchestrationCard from "./StartOrchestrationCard";
import {ICDBPageProps} from "../../utils/ICDBPageProps";


export const OPTION_MAILROOM: IDropdownOption = {text: "Mailroom", key: "mailroom", data: "mailroom"};
// export const OPTION_WORKLOADS: IDropdownOption = {text: "Workloads", key: "workloads", data: "workloads"};

export interface DurableFunctionBrowserListPageProps extends ICDBPageProps{
    queryString?: string
    apiService: ApiService;
    history: H.History;
}

export interface DurableFunctionBrowserListPageState {
    loading: boolean;
    instanceIdPrefix?: string;
    instances?: DurableFunctionInstance[];
    appName: DurableFunctionAppName;
}

export default class DurableFunctionBrowserListPage extends Component<DurableFunctionBrowserListPageProps, DurableFunctionBrowserListPageState> {
    static displayName = DurableFunctionBrowserListPage.name;

    private readonly debouncedGetInstances: debounce<() => void>;
    
    constructor(props: DurableFunctionBrowserListPageProps) {
        super(props);

        this.debouncedGetInstances = debounce(200,this.getInstances);
        
        this.state = {
            loading: false,
            instances: undefined,
            appName: "mailroom"
        };
    }

    public async componentDidMount() {
        await this.getInstances();
    }

    public async componentDidUpdate(prevProps: Readonly<DurableFunctionBrowserListPageProps & DispatchProps>, prevState: Readonly<DurableFunctionBrowserListPageState>, snapshot?: any): Promise<void> {

        if (this.state.appName !== prevState.appName || this.state.instanceIdPrefix !== prevState.instanceIdPrefix) {
            this.debouncedGetInstances();
        }
        
        if(this.props.queryString !== prevProps.queryString && this.props.queryString)
        {
            const instanceIdPrefix = parseQueryString(this.props.queryString)["instanceIdPrefix"]
            this.setState({
                instanceIdPrefix: instanceIdPrefix
            });
        }
        
    }

    
    private async getInstances() {

        this.setState({
            loading: true
        });

        try
        {
            const query = this.state.instanceIdPrefix ? {instanceIdPrefix: this.state.instanceIdPrefix} : undefined;
            const instances = await this.props.apiService.getMailroomDurableFunctionInstances(query);

            this.setState({
                instances: instances,
                loading: false
            });

        }
        catch (e) {
            console.log(e);
            this.props.messageBar.error(`Failed to get mailroom durable function instances : ${e}`,undefined);
            this.setState({
                instances: []
            })
        }
    }
    
    private tryWithMessageBar = async (func: Function,description: string): Promise<void> =>
    {
        try
        {
            await func();
            this.props.messageBar.success(`${description} succeeded`, undefined);
        }
        catch (e)
        {
            this.props.messageBar.error(`Failed to ${description} : ${e}`, undefined);
        }
    }
    
    private cellForColumn = (item: DurableFunctionInstance, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined => {
        
        if (column.propertyPath === "actions")
        {
            return (
                <TableCell flex={column.flex}>
                    <DefaultButton text="Actions" menuProps={{
                        items: [
                            { 
                                key: 'terminate',
                                text: 'Terminate', 
                                onClick: () => {
                                    this.tryWithMessageBar(async () => this.props.apiService.terminateMailroomDurableFunction(item.durableFunctionInstanceId),`Terminate ${item.durableFunctionInstanceId}`)
                                }
                            }
                        ]
                    }} />
                </TableCell>
            )
        }
        return undefined;
    }
    
    public render() {
        return (
            <Fragment>
                
                <StartOrchestrationCard apiService={this.props.apiService} selectedApp={"mailroom"} messageBar={this.props.messageBar}/>
                
                <div className="filter-controls p-top-1rem">
                    <div className="filter-controls__row">
                        <TextField 
                            className="filter-controls__input"
                            autoFocus
                            label="Instance Id Prefix"
                            placeholder="Prefix"
                            value={this.state.instanceIdPrefix}
                            onChange={
                               (event, newValue) => {
                                   const newQueryString = ApiService.buildQueryString({instanceIdPrefix: newValue});
                                   this.props.history.push({ search: newQueryString });
                                }
                           }/>
                    </div>
                </div>

                <MailroomDurableFunctionInstanceList
                    title={`Durable Functions (${this.state.appName})`}
                    enableRowLinks={true}
                    linkForInstance={i => `/cdb-admin/durable-functions/instances/${i.durableFunctionInstanceId}`}
                    instances={this.state.instances}
                    columns={[...MailroomDurableFunctionInstanceList.genericColumns, {
                        title: "Actions",
                        propertyPath: "actions",
                        flex: 1
                    }]}
                    additionalCellForColumn={this.cellForColumn}>

                    <Dropdown
                        className={"m-right-1rem"}
                        options={[
                            OPTION_MAILROOM,
                            // OPTION_WORKLOADS
                        ]}
                        selectedKey={this.state.appName}
                        onChange={(e, selected) => {
                            const appName = selected?.data as DurableFunctionAppName;
                            this.setState({
                                appName
                            });
                        }}
                    />
                </MailroomDurableFunctionInstanceList>
            </Fragment>
        );
    };
}